import { create } from "zustand";
import { api, wsUrl } from "../lib/api";
import { playSfxAsset, triggerSound } from "../lib/soundBus";

export const useGameStore = create((set, get) => ({
  game: null,
  tokens: [],
  templates: [],
  obstacles: [],
  walls: [],
  doors: [],
  myMemberId: null,
  pendingObstacleTrigger: null,
  pendingSightConeTrigger: null,
  pendingFreeRoamInvite: null,
  pendingRangeApproval: null,
  pendingDoorTrigger: null,
  pendingDoorTriggerCheck: null,
  pendingPortalTransition: null,
  pendingGameShowPortal: null,
  pendingTemplateTrigger: null,
  talkToGmPing: null,
  combatAlertPing: false,
  pendingNpcAttack: null,
  pendingAnimationTrigger: null,
  lastCombatSummary: null,
  grantedRangeApprovals: {},
  voiceWhisper: { active: false, gm_member_id: null, gm_user_id: null, target_member_id: null, target_user_id: null },
  chatMessages: [],
  online: 0,
  gm_online: true,
  socket: null,
  activeSceneId: null,
  selectedTokenId: null,
  combat: { active: false, round: 1, turn_index: 0, order: [] },
  free_roam: { enabled: false, pace: "walk", hustle_seconds_remaining: null, movement_pool_ft: 0, clock_minutes: 480, clock_day: 1, encounters: [] },

  setSelectedTokenId: (id) => set({ selectedTokenId: id }),
  setMyMemberId: (id) => set({ myMemberId: id }),
  setPendingObstacleTrigger: (data) => set({ pendingObstacleTrigger: data }),
  setPendingSightConeTrigger: (data) => set({ pendingSightConeTrigger: data }),
  setPendingRangeApproval: (data) => set({ pendingRangeApproval: data }),
  setPendingDoorTrigger: (data) => set({ pendingDoorTrigger: data }),
  setPendingDoorTriggerCheck: (data) => set({ pendingDoorTriggerCheck: data }),
  setPendingPortalTransition: (data) => set({ pendingPortalTransition: data }),
  setPendingGameShowPortal: (data) => set({ pendingGameShowPortal: data }),
  setPendingTemplateTrigger: (data) => set({ pendingTemplateTrigger: data }),
  setTalkToGmPing: (data) => set({ talkToGmPing: data }),
  setCombatAlertPing: (v) => set({ combatAlertPing: v }),
  setPendingNpcAttack: (data) => set({ pendingNpcAttack: data }),
  setVoiceWhisper: (data) => set({ voiceWhisper: data || { active: false, gm_member_id: null, gm_user_id: null, target_member_id: null, target_user_id: null } }),
  setGame: (game) =>
    set((s) => {
      const stillValid = s.activeSceneId && (game?.scenes || []).some((sc) => sc.id === s.activeSceneId);
      return { game, activeSceneId: stillValid ? s.activeSceneId : game?.current_scene_id || null, combat: game?.combat || { active: false, round: 1, turn_index: 0, order: [] }, free_roam: game?.free_roam || get().free_roam, lastCombatSummary: game?.last_combat_summary || null };
    }),
  setActiveScene: (sceneId) => set({ activeSceneId: sceneId }),

  fetchGame: async (gameId) => {
    const { data } = await api.get(`/games/${gameId}`);
    set((s) => {
      const stillValid = s.activeSceneId && (data?.scenes || []).some((sc) => sc.id === s.activeSceneId);
      return { game: data, activeSceneId: stillValid ? s.activeSceneId : data.current_scene_id, combat: data.combat || { active: false, round: 1, turn_index: 0, order: [] }, free_roam: data.free_roam || get().free_roam, lastCombatSummary: data.last_combat_summary || null, pendingNpcAttack: data.pending_npc_attack || null };
    });
    return data;
  },

  fetchTokens: async (gameId, sceneId) => {
    if (!sceneId) return;
    const { data } = await api.get(`/games/${gameId}/tokens`, { params: { scene_id: sceneId } });
    set({ tokens: data });
  },

  fetchTemplates: async (gameId, sceneId) => {
    if (!sceneId) return;
    const { data } = await api.get(`/games/${gameId}/templates`, { params: { scene_id: sceneId } });
    set({ templates: data });
  },

  fetchObstacles: async (gameId, sceneId) => {
    if (!sceneId) return;
    try {
      const { data } = await api.get(`/games/${gameId}/obstacles`, { params: { scene_id: sceneId } });
      set({ obstacles: data });
    } catch {
      set({ obstacles: [] });
    }
  },

  fetchWalls: async (gameId, sceneId) => {
    if (!sceneId) return;
    const { data } = await api.get(`/games/${gameId}/walls`, { params: { scene_id: sceneId } });
    set({ walls: data });
  },

  fetchDoors: async (gameId, sceneId) => {
    if (!sceneId) return;
    const { data } = await api.get(`/games/${gameId}/doors`, { params: { scene_id: sceneId } });
    set({ doors: data });
  },

  fetchChat: async (gameId) => {
    const { data } = await api.get(`/games/${gameId}/chat`);
    set({ chatMessages: data });
  },

  upsertToken: (token) =>
    set((s) => {
      const exists = s.tokens.some((t) => t.id === token.id);
      return { tokens: exists ? s.tokens.map((t) => (t.id === token.id ? token : t)) : [...s.tokens, token] };
    }),

  removeToken: (id) => set((s) => ({ tokens: s.tokens.filter((t) => t.id !== id) })),

  upsertTemplate: (tpl) =>
    set((s) => {
      const exists = s.templates.some((t) => t.id === tpl.id);
      return { templates: exists ? s.templates.map((t) => (t.id === tpl.id ? tpl : t)) : [...s.templates, tpl] };
    }),

  removeTemplate: (id) => set((s) => ({ templates: s.templates.filter((t) => t.id !== id) })),

  upsertObstacle: (obs) =>
    set((s) => {
      const exists = s.obstacles.some((o) => o.id === obs.id);
      return { obstacles: exists ? s.obstacles.map((o) => (o.id === obs.id ? obs : o)) : [...s.obstacles, obs] };
    }),

  removeObstacle: (id) => set((s) => ({ obstacles: s.obstacles.filter((o) => o.id !== id) })),

  upsertScene: (scene) =>
    set((s) => (s.game ? { game: { ...s.game, scenes: (s.game.scenes || []).map((sc) => (sc.id === scene.id ? scene : sc)) } } : {})),

  upsertWall: (wall) => set((s) => ({
    walls: s.walls.some((w) => w.id === wall.id) ? s.walls.map((w) => (w.id === wall.id ? wall : w)) : [...s.walls, wall],
  })),
  removeWall: (id) => set((s) => ({ walls: s.walls.filter((w) => w.id !== id) })),

  upsertDoor: (door) =>
    set((s) => {
      const exists = s.doors.some((d) => d.id === door.id);
      return { doors: exists ? s.doors.map((d) => (d.id === door.id ? door : d)) : [...s.doors, door] };
    }),
  removeDoor: (id) => set((s) => ({ doors: s.doors.filter((d) => d.id !== id) })),

  addScene: (scene) =>
    set((s) => {
      if (!s.game) return {};
      if ((s.game.scenes || []).some((sc) => sc.id === scene.id)) return {};
      return { game: { ...s.game, scenes: [...(s.game.scenes || []), scene] } };
    }),

  addChatMessage: (msg) =>
    set((s) => (s.chatMessages.some((m) => m.id === msg.id) ? s : { chatMessages: [...s.chatMessages, msg] })),

  markWhisperRead: ({ reader_member_id, other_member_id, read_at }) =>
    set((s) => ({
      chatMessages: s.chatMessages.map((m) =>
        m.type === "whisper" && m.sender_member_id === other_member_id && m.whisper_target_member_id === reader_member_id && !m.read_at
          ? { ...m, read_at }
          : m
      ),
    })),

  _gen: 0,

  connectWebSocket: async (gameId) => {
    const myGen = get()._gen + 1;
    const existing = get().socket;
    if (existing) {
      existing.onclose = null;
      existing.close();
    }
    set({ _gen: myGen, socket: null });
    const { data } = await api.get("/auth/ws-token");
    if (get()._gen !== myGen) return; // superseded by a newer connect/disconnect call
    const socket = new WebSocket(wsUrl(gameId, data.token));
    socket.onclose = () => {
      if (get().socket === socket) set({ socket: null });
    };
    socket.onmessage = (event) => {
      if (get()._gen !== myGen) return;
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "token_create" || msg.type === "token_update") {
          const incoming = msg.data;
          const previous = get().tokens.find((t) => t.id === incoming.id);
          if (
            msg.type === "token_update" && previous && incoming.death_sfx_asset_id &&
            (previous.hp_current ?? 1) > 0 && incoming.hp_current != null && incoming.hp_current <= 0
          ) {
            playSfxAsset(incoming.death_sfx_asset_id);
          }
          // Animated tokens: derive Death/Resurrection/Getting-Hit clip triggers straight off the
          // same hp_current transition used for the death sound above — every connected client
          // sees the identical hp_current change, so this stays perfectly in sync with no extra
          // websocket event needed. "Attack" clips are triggered explicitly instead (see the
          // `token_animation_trigger` case below), since there's no state change to detect them from.
          if (msg.type === "token_update" && previous && incoming.is_animated && incoming.animation_clips) {
            const prevHp = previous.hp_current;
            const newHp = incoming.hp_current;
            const clips = incoming.animation_clips;
            if (clips.death && (prevHp ?? 1) > 0 && newHp != null && newHp <= 0) {
              set({ pendingAnimationTrigger: { token_id: incoming.id, clip: "death", trigger_id: `hp-death-${incoming.id}-${Date.now()}` } });
            } else if (clips.resurrection && prevHp != null && prevHp <= 0 && newHp != null && newHp > 0) {
              set({ pendingAnimationTrigger: { token_id: incoming.id, clip: "resurrection", trigger_id: `hp-res-${incoming.id}-${Date.now()}` } });
            } else if (clips.hit && prevHp != null && newHp != null && newHp < prevHp && newHp > 0) {
              set({ pendingAnimationTrigger: { token_id: incoming.id, clip: "hit", trigger_id: `hp-hit-${incoming.id}-${Date.now()}` } });
            }
          }
          // If MY OWN token just stepped through a door into a different scene, follow it —
          // this is how a solo player in Free Roam genuinely ends up on a different live map.
          if (incoming.created_by && incoming.created_by === get().myMemberId && incoming.scene_id !== get().activeSceneId) {
            set({ activeSceneId: incoming.scene_id });
          }
          const belongsHere = incoming.scene_id === get().activeSceneId;
          set((s) => {
            const exists = s.tokens.some((t) => t.id === incoming.id);
            if (belongsHere) {
              return { tokens: exists ? s.tokens.map((t) => (t.id === incoming.id ? incoming : t)) : [...s.tokens, incoming] };
            }
            return exists ? { tokens: s.tokens.filter((t) => t.id !== incoming.id) } : {};
          });
        }
        else if (msg.type === "token_delete") get().removeToken(msg.data.id);
        else if (msg.type === "template_create" || msg.type === "template_update") get().upsertTemplate(msg.data);
        else if (msg.type === "template_delete") get().removeTemplate(msg.data.id);
        else if (msg.type === "obstacle_create" || msg.type === "obstacle_update") get().upsertObstacle(msg.data);
        else if (msg.type === "obstacle_delete") get().removeObstacle(msg.data.id);
        else if (msg.type === "door_create" || msg.type === "door_update") get().upsertDoor(msg.data);
        else if (msg.type === "doors_bulk_update") msg.data.forEach((d) => get().upsertDoor(d));
        else if (msg.type === "door_delete") get().removeDoor(msg.data.id);
        else if (msg.type === "door_trigger") set({ pendingDoorTrigger: msg.data });
        else if (msg.type === "door_trigger_check") set({ pendingDoorTriggerCheck: msg.data });
        else if (msg.type === "portal_transition") set({ pendingPortalTransition: msg.data });
        else if (msg.type === "gameshow_portal") set({ pendingGameShowPortal: msg.data });
        else if (msg.type === "template_trigger") set({ pendingTemplateTrigger: msg.data });
        else if (msg.type === "game_scene_switch") set((s) => ({
          activeSceneId: s.activeSceneId === msg.data.from_scene_id ? msg.data.to_scene_id : s.activeSceneId,
          game: s.game ? { ...s.game, current_scene_id: msg.data.to_scene_id } : s.game,
        }));
        else if (msg.type === "scene_create") get().addScene(msg.data);
        else if (msg.type === "obstacle_trigger") set({ pendingObstacleTrigger: { ...msg.data, gm_modifier: 0 } });
        else if (msg.type === "obstacle_modifier_set") set((s) => (
          s.pendingObstacleTrigger?.obstacle_id === msg.data.obstacle_id && s.pendingObstacleTrigger?.member_id === msg.data.member_id
            ? { pendingObstacleTrigger: { ...s.pendingObstacleTrigger, gm_modifier: msg.data.modifier } }
            : {}
        ));
        else if (msg.type === "sight_cone_trigger") set({ pendingSightConeTrigger: msg.data });
        else if (msg.type === "talk_to_gm_ping") set({ talkToGmPing: msg.data });
        else if (msg.type === "npc_attack_pending") set({ pendingNpcAttack: msg.data });
        else if (msg.type === "npc_attack_pending_clear") set((s) => (s.pendingNpcAttack?.npc_token_id === msg.data.npc_token_id ? { pendingNpcAttack: null } : {}));
        else if (msg.type === "attack_sfx") triggerSound(gameId, { tag: msg.data.tag, overrideAssetId: msg.data.override_asset_id });
        else if (msg.type === "voice_whisper") set({ voiceWhisper: msg.data });
        else if (msg.type === "range_approval_request") set({ pendingRangeApproval: msg.data });
        else if (msg.type === "range_approval_grant") set((s) => ({
          grantedRangeApprovals: { ...s.grantedRangeApprovals, [msg.data.request_id]: true },
          pendingRangeApproval: s.pendingRangeApproval?.request_id === msg.data.request_id ? null : s.pendingRangeApproval,
        }));
        else if (msg.type === "wall_create") get().upsertWall(msg.data);
        else if (msg.type === "wall_update") get().upsertWall(msg.data);
        else if (msg.type === "wall_delete") get().removeWall(msg.data.id);
        else if (msg.type === "chat_message") get().addChatMessage(msg.data);
        else if (msg.type === "presence") set({ online: msg.data.online, gm_online: msg.data.gm_online });
        else if (msg.type === "combat_update") set({ combat: msg.data });
        else if (msg.type === "game_update") set((s) => ({ game: s.game ? { ...s.game, ...msg.data } : s.game }));
        else if (msg.type === "combat_summary") set({ lastCombatSummary: msg.data });
        else if (msg.type === "free_roam_update") set((s) => {
          const oldIds = new Set((s.free_roam.encounters || []).map((e) => e.id));
          const hasNewEncounter = (msg.data.encounters || []).some((e) => !oldIds.has(e.id));
          return { free_roam: msg.data, combatAlertPing: hasNewEncounter ? true : s.combatAlertPing };
        });
        else if (msg.type === "free_roam_invite") set({ pendingFreeRoamInvite: msg.data });
        else if (msg.type === "scene_update") get().upsertScene(msg.data);
        else if (msg.type === "member_joined") {
          set((s) => {
            if (!s.game || s.game.members?.some((m) => m.id === msg.data.id)) return {};
            return { game: { ...s.game, members: [...(s.game.members || []), msg.data] } };
          });
        }
        else if (msg.type === "whisper_read") get().markWhisperRead(msg.data);
        else if (msg.type === "token_animation_trigger") set({ pendingAnimationTrigger: msg.data });
        else if (msg.type === "sheet_update") { /* consumed by character sheet component polling */ }
      } catch {}
    };
    if (get()._gen !== myGen) {
      socket.close();
      return;
    }
    set({ socket });
  },

  disconnectWebSocket: () => {
    set({ _gen: get()._gen + 1 });
    const s = get().socket;
    if (s) {
      s.onclose = null;
      s.close();
    }
    set({ socket: null });
  },

  sendWs: (payload) => {
    const s = get().socket;
    if (s && s.readyState === WebSocket.OPEN) s.send(JSON.stringify(payload));
  },
}));
